import React, { useState, useEffect } from 'react';
import { Calendar, Clock, DollarSign, Users, CheckCircle, XCircle, Eye, Star, TrendingUp, Bell, ArrowLeft, Home, Download, FileText, Shield } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext.tsx';
import TrainerAvailabilityManager from './TrainerAvailabilityManager.tsx';
import TrainerPricingManager from './TrainerPricingManager.tsx';
import { BookingDashboard } from './BookingDashboard.tsx';
import { StripeConnectOnboarding } from './StripeConnectOnboarding.tsx';
import api, { stripeAPI } from '../services/api.ts';
import TrainerResourcesManager from './TrainerResourcesManager.tsx';
import { MessagingIconSimple } from './messaging/MessagingIconSimple.tsx';



// TypeScript interfaces
interface PendingBooking {
  id: number;
  client: string;
  service: string;
  date: string;
  time: string;
  duration: string;
  rate: number;
  message: string;
  avatar: string;
  location?: string;
  customer_profile_picture?: string;
}

interface ConfirmedBooking {
  id: number;
  customer_id: string;
  client: string;
  service: string;
  date: string;
  time: string;
  status: string;
  avatar: string;
  customer_address?: string;
  customer_phone?: string;
  customer_profile_picture?: string;
  notes?: string;
}

interface EarningsData {
  today: number;
  week: number;
  month: number;
  pending: number;
}

interface Client {
  id: string;
  name: string;
  username: string;
  sessions: number;
  lastSeen: string;
  rating: number;
  avatar: string;
  profile_picture?: string;
}

interface TrainerDashboardProps {
  onNavigateBack: () => void;
}

const API_BASE_URL = 'https://aztrainersbackend.onrender.com/api';

const TrainerDashboard: React.FC<TrainerDashboardProps> = ({ onNavigateBack }) => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('pending');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(true);
  const [stripeConnected, setStripeConnected] = useState(false);
  const [stripeLoading, setStripeLoading] = useState(true);
  const [trainerStripeAccountId, setTrainerStripeAccountId] = useState<string>('');
  const [pendingBookings, setPendingBookings] = useState<PendingBooking[]>([]);
  const [confirmedBookings, setConfirmedBookings] = useState<ConfirmedBooking[]>([]);
  const [stripeStatus, setStripeStatus] = useState<any>(null);
  const [earningsData, setEarningsData] = useState<EarningsData>({
    today: 0,
    week: 0,
    month: 0,
    pending: 0
  });
  const [recentClients, setRecentClients] = useState<Client[]>([]);


  useEffect(() => {
    if (user && user.role === 'trainer') {
      fetchTrainerData();
    }
  }, [user]);

  useEffect(() => {
    if (user && user.role === 'trainer') {
      if (activeTab === 'schedule') {
        fetchScheduleForDate(selectedDate);
      } else if (activeTab === 'pending') {
        fetchPendingRequests();
      }
    }
  }, [selectedDate, activeTab, user]);




  // Add this useEffect to refetch schedule when date changes
  const fetchScheduleForDate = async (date: string): Promise<void> => {
    try {
      const authToken = localStorage.getItem('authToken');
      console.log('Sending auth token:', authToken);

      const response = await fetch(`${API_BASE_URL}/trainer/schedule/?date=${date}`, {
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${authToken}`, // Changed to Token instead of Bearer
          'Content-Type': 'application/json',
        },
      });

      console.log('Response status:', response.status);

      if (response.ok) {
        const data = await response.json(); // Only call .json() once
        console.log('Schedule data:', data);
        setConfirmedBookings(data.schedule || []);
      } else {
        const errorText = await response.text(); // Only call .text() for errors
        console.error('Failed to fetch schedule:', response.statusText, errorText);
        setConfirmedBookings([]);
      }
    } catch (error) {
      console.error('Failed to fetch schedule:', error);
      setConfirmedBookings([]);
    }
  };


  const fetchStripeStatus = async (): Promise<void> => {
    try {
      const response = await stripeAPI.checkConnectStatus();
      const data = response.data;

      // Store the complete status data
      setStripeStatus(data);

      // Keep your existing logic
      setStripeConnected(data.connected && data.onboarding_complete && data.charges_enabled);
      setTrainerStripeAccountId(data.account_id || '');
    } catch (error) {
      console.error('Failed to fetch Stripe status:', error);
      setStripeConnected(false);
      setStripeStatus(null); // Add this
    } finally {
      setStripeLoading(false);
    }
  };


  const fetchTrainerData = async () => {
    try {
      setLoading(true);

      await fetchStripeStatus();
      await fetchPendingRequests();
      await fetchScheduleForDate(selectedDate);
      await fetchEarningsData();
      await fetchClientsData();


    } catch (error) {
      console.error('Failed to fetch trainer data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingRequests = async (): Promise<void> => {
    try {
      const authToken = localStorage.getItem('authToken');

      const response = await fetch(`${API_BASE_URL}/trainer/pending-requests/`, {
        credentials: 'include',
        headers: {
          'Authorization': `Token ${authToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setPendingBookings(data.pending_requests || []);
      } else {
        console.error('Failed to fetch pending requests:', response.statusText);
        setPendingBookings([]); // Empty array if API fails
      }
    } catch (error) {
      console.error('Failed to fetch pending requests:', error);
      setPendingBookings([]); // Empty array if API fails
    }
  };

  const fetchEarningsData = async (): Promise<void> => {
    try {
      const authToken = localStorage.getItem('authToken');

      const response = await fetch(`${API_BASE_URL}/trainer/earnings/`, {
        credentials: 'include',
        headers: {
          'Authorization': `Token ${authToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setEarningsData(data.earnings || { today: 0, week: 0, month: 0, pending: 0 });
      } else {
        console.error('Failed to fetch earnings:', response.statusText);
        setEarningsData({ today: 0, week: 0, month: 0, pending: 0 });
      }
    } catch (error) {
      console.error('Failed to fetch earnings:', error);
      setEarningsData({ today: 0, week: 0, month: 0, pending: 0 });
    }
  };

  const fetchClientsData = async (): Promise<void> => {
    try {
      const authToken = localStorage.getItem('authToken');

      const response = await fetch(`${API_BASE_URL}/trainer/clients/`, {
        credentials: 'include',
        headers: {
          'Authorization': `Token ${authToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setRecentClients(data.clients || []);
      } else {
        console.error('Failed to fetch clients:', response.statusText);
        setRecentClients([]); // Empty array if API fails
      }
    } catch (error) {
      console.error('Failed to fetch clients:', error);
      setRecentClients([]); // Empty array if API fails
    }
  };

  const handleBookingAction = async (bookingId: number, action: string) => {
    try {
      const authToken = localStorage.getItem('authToken');
      const endpoint = action === 'confirm' ? 'accept' : 'decline';

      let body = {};
      if (action === 'decline') {
        const reason = prompt('Please provide a reason for declining (optional):');
        body = { reason: reason || '' };
      }

      const response = await fetch(`${API_BASE_URL}/bookings/${bookingId}/${endpoint}/`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Authorization': `Token ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (response.ok) {
        // Remove from pending requests immediately
        setPendingBookings(prev => prev.filter(b => b.id !== bookingId));

        // Show success message
        alert(`Booking ${action === 'confirm' ? 'accepted' : 'declined'} successfully!`);

        // Refresh all data
        await fetchPendingRequests();
        await fetchScheduleForDate(selectedDate);
        await fetchEarningsData();
      } else {
        alert(`Failed to ${action} booking. Please try again.`);
      }
    } catch (error) {
      console.error(`Failed to ${action} booking:`, error);
      alert(`Failed to ${action} booking. Please try again.`);
    }
  };


  const handleCompleteVerification = async () => {
    try {
      const response = await api.post('/stripe/connect/create-verification-link/');
      if (response.data.verification_url) {
        window.open(response.data.verification_url, '_blank');
      }
    } catch (error) {
      console.error('Error creating verification link:', error);

    }
  };


  const handleManagePayments = async () => {
    try {
      const response = await api.post('/stripe/connect/create-login-link/');
      if (response.data.login_url) {
        window.open(response.data.login_url, '_blank');
      }
    } catch (error) {
      console.error('Error creating login link:', error);
    }
  };



  const getTabColor = (tabId: string) => {
    switch (tabId) {
      case 'pending':
        return activeTab === tabId ? 'bg-[#E6A756] text-[#5D4037]' : 'bg-[#F8F4EE] text-[#5D4037] hover:bg-[#F5EFE7] border-r border-[#D4C4B0]';
      case 'schedule':
        return activeTab === tabId ? 'bg-[#9CAF88] text-[#FEFCF8]' : 'bg-[#F8F4EE] text-[#5D4037] hover:bg-[#F5EFE7] border-r border-[#D4C4B0]';
      case 'availability':
        return activeTab === tabId ? 'bg-[#B8C5A6] text-[#5D4037]' : 'bg-[#F8F4EE] text-[#5D4037] hover:bg-[#F5EFE7] border-r border-[#D4C4B0]';
      case 'clients':
        return activeTab === tabId ? 'bg-[#9CAF88] text-[#FEFCF8]' : 'bg-[#F8F4EE] text-[#5D4037] hover:bg-[#F5EFE7] border-r border-[#D4C4B0]';
      case 'earnings':
        return activeTab === tabId ? 'bg-[#A0673D] text-[#FEFCF8]' : 'bg-[#F8F4EE] text-[#5D4037] hover:bg-[#F5EFE7] border-r border-[#D4C4B0]';
      case 'pricing':
        return activeTab === tabId ? 'bg-[#8B5A3C] text-[#FEFCF8]' : 'bg-[#F8F4EE] text-[#5D4037] hover:bg-[#F5EFE7] border-r border-[#D4C4B0]';
      case 'bookings':
        return activeTab === tabId ? 'bg-[#D2824C] text-[#FEFCF8]' : 'bg-[#F8F4EE] text-[#5D4037] hover:bg-[#F5EFE7] border-r border-[#D4C4B0]';
      case 'resources':
        return activeTab === tabId ? 'bg-[#7A8471] text-[#FEFCF8]' : 'bg-[#F8F4EE] text-[#5D4037] hover:bg-[#F5EFE7] border-r border-[#D4C4B0]';
      default:
        return 'bg-[#F8F4EE] text-[#5D4037] hover:bg-[#F5EFE7]';
    }
  };

  if (!user || user.role !== 'trainer') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h2>
          <p className="text-gray-600 mb-4">This dashboard is only available for trainers.</p>
          <button
            onClick={onNavigateBack}
            className="bg-[#E6A756] text-[#5D4037] px-4 py-2 rounded-lg hover:bg-[#D2824C] transition-colors font-medium"
          >
            Return to Home
          </button>

        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#E6A756]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header with Navigation */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={onNavigateBack}
                className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft className="h-5 w-5 mr-2" />
                Back to Main
              </button>
              <div className="border-l border-gray-300 pl-4">
                <h1 className="text-2xl font-bold text-gray-900">Trainer Dashboard</h1>
                <p className="text-gray-600">Welcome back, {user.first_name}!</p>
              </div>
            </div>


            <div className="flex items-center space-x-4">
              <div className="relative">
                <Bell className="h-6 w-6 text-gray-400 cursor-pointer hover:text-gray-600" />
                <div className="absolute -top-2 -right-2 bg-[#D2824C] text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {pendingBookings.length}
                </div>
              </div>
              <div className="flex items-center space-x-2">
                {/* Updated Profile Picture Section */}
                <div className="w-8 h-8 rounded-full overflow-hidden">
                  {user.profile_picture ? (
                    <img
                      src={user.profile_picture}
                      alt={`${user.first_name} ${user.last_name}`}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-[#E6A756] to-[#D2824C] flex items-center justify-center text-white font-semibold">
                      {user.first_name?.[0]}{user.last_name?.[0]}
                    </div>
                  )}
                </div>
                <span className="text-gray-700 font-medium">{user.first_name} {user.last_name}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stripe Connect Warning Banner */}
      {!stripeLoading && !stripeConnected && (
        <div className="max-w-7xl mx-auto px-4 pt-6">
          <StripeConnectOnboarding />
        </div>
      )}


      {/*Stripe Verification Banner */}
      {!stripeLoading && stripeStatus && stripeStatus.account_id && !stripeStatus.charges_enabled && (
        <div className="max-w-7xl mx-auto px-4 pt-6">
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-lg">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-medium text-yellow-800">
                  Complete Stripe Verification Required
                </h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>Your Stripe account needs additional verification before you can receive payments. Please complete the verification process.</p>
                </div>
                <div className="mt-4">
                  <button
                    onClick={handleCompleteVerification}
                    className="bg-yellow-600 hover:bg-yellow-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 flex items-center"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Complete Verification
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quick Stats */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <DollarSign className="h-8 w-8 text-[#8B5A3C]" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Today's Earnings</p>
                <p className="text-2xl font-bold text-gray-900">${earningsData.today}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <TrendingUp className="h-8 w-8 text-[#9CAF88]" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">This Week</p>
                <p className="text-2xl font-bold text-gray-900">${earningsData.week}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <Clock className="h-8 w-8 text-[#E6A756]" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Pending Requests</p>
                <p className="text-2xl font-bold text-gray-900">{pendingBookings.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <Users className="h-8 w-8 text-[#A0673D]" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Active Clients</p>
                <p className="text-2xl font-bold text-gray-900">{recentClients.length}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="flex border-b">
            <button
              onClick={() => setActiveTab('pending')}
              className={`px-6 py-3 font-medium transition-colors ${getTabColor('pending')}`}
            >
              <Clock className="h-4 w-4 inline mr-2" />
              Pending Requests ({pendingBookings.length})
            </button>
            <button
              onClick={() => setActiveTab('schedule')}
              className={`px-6 py-3 font-medium transition-colors ${getTabColor('schedule')}`}
            >
              <Calendar className="h-4 w-4 inline mr-2" />
              Schedule
            </button>
            <button
              onClick={() => setActiveTab('clients')}
              className={`px-6 py-3 font-medium transition-colors ${getTabColor('clients')}`}
            >
              <Users className="h-4 w-4 inline mr-2" />
              Clients ({recentClients.length})
            </button>

            <button
              onClick={() => setActiveTab('bookings')}
              className={`px-6 py-3 font-medium transition-colors ${getTabColor('bookings')}`}
            >
              <Calendar className="h-4 w-4 inline mr-2" />
              Bookings
            </button>

            <button
              onClick={() => setActiveTab('availability')}
              className={`px-6 py-3 font-medium transition-colors ${getTabColor('availability')}`}
            >
              <Calendar className="h-4 w-4 inline mr-2" />
              Availability
            </button>
            <button
              onClick={() => setActiveTab('earnings')}
              className={`px-6 py-3 font-medium transition-colors ${getTabColor('earnings')}`}
            >
              <DollarSign className="h-4 w-4 inline mr-2" />
              Earnings
            </button>

            <button
              onClick={() => setActiveTab('pricing')}
              className={`px-6 py-3 font-medium transition-colors ${getTabColor('pricing')}`}
            >
              <DollarSign className="h-4 w-4 inline mr-2" />
              Pricing
            </button>

            <button
              onClick={() => setActiveTab('resources')}
              className={`px-6 py-3 font-medium transition-colors ${getTabColor('resources')}`}
            >
              <Download className="h-4 w-4 inline mr-2" />
              Resources
            </button>


          </div>

          <div className="p-6">
            {/* Pending Requests Tab */}
            {activeTab === 'pending' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Booking Requests Awaiting Your Response
                </h3>
                {pendingBookings.length === 0 ? (
                  <div className="text-center py-8">
                    <Clock className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No pending requests at the moment</p>
                    <p className="text-sm text-gray-400">New booking requests will appear here</p>
                  </div>
                ) : (
                  pendingBookings.map((booking) => (
                    <div key={booking.id} className="border border-[#D4C4B0] rounded-lg p-6 bg-[#F8F4EE]">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-4">
                          {/* Customer Profile Picture - matching schedule section */}
                          <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0">
                            {booking.customer_profile_picture ? (
                              <img
                                src={booking.customer_profile_picture}
                                alt={booking.client}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full bg-gradient-to-br from-[#E6A756] to-[#D2824C] flex items-center justify-center text-white font-bold text-lg">
                                {booking.client.split(' ').map(n => n[0]).join('')}
                              </div>
                            )}
                          </div>

                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-2">
                              <h4 className="font-semibold text-gray-900">{booking.client}</h4>
                              <span className="text-sm text-gray-500">•</span>
                              <span className="text-sm text-gray-600">{booking.service}</span>
                            </div>
                            <div className="flex items-center space-x-4 text-sm text-gray-600 mb-3">
                              <span className="flex items-center">
                                <Calendar className="h-4 w-4 mr-1" />
                                {booking.date}
                              </span>
                              <span className="flex items-center">
                                <Clock className="h-4 w-4 mr-1" />
                                {booking.time} ({booking.duration})
                              </span>
                              <span className="flex items-center text-green-600 font-semibold">
                                <DollarSign className="h-4 w-4 mr-1" />
                                ${booking.rate}
                              </span>
                            </div>

                            {/* Location */}
                            {booking.location && (
                              <div className="flex items-start text-sm text-gray-600 mb-2">
                                <svg className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                <div>
                                  <span className="font-medium text-gray-700">Location:</span>
                                  <span className="ml-1">{booking.location}</span>
                                </div>
                              </div>
                            )}

                            {/* Notes/Message */}
                            {booking.message && (
                              <div className="bg-white rounded-lg p-3 mb-4 border border-gray-200">
                                <div className="flex items-start">
                                  <svg className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012 2v8a2 2 0 01-2 2h-1l-4 4z" />
                                  </svg>
                                  <div>
                                    <span className="font-medium text-gray-700 text-sm">Customer Notes:</span>
                                    <p className="text-sm text-gray-600 mt-1 italic">"{booking.message}"</p>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex space-x-2 ml-4">
                          <button
                            onClick={() => handleBookingAction(booking.id, 'confirm')}
                            className="flex items-center px-4 py-2 bg-[#9CAF88] text-[#FEFCF8] rounded-lg hover:bg-[#7A8471] transition-colors font-medium"
                          >
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Accept
                          </button>
                          <button
                            onClick={() => handleBookingAction(booking.id, 'decline')}
                            className="flex items-center px-4 py-2 bg-[#A0673D] text-[#FEFCF8] rounded-lg hover:bg-[#8B5A3C] transition-colors font-medium"
                          >
                            <XCircle className="h-4 w-4 mr-2" />
                            Decline
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Schedule Tab */}
            {activeTab === 'schedule' && (
              <div>
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">Your Schedule</h3>
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="border border-gray-300 rounded-lg px-3 py-2"
                  />
                </div>
                <div className="space-y-4">
                  {confirmedBookings.length === 0 ? (
                    <div className="text-center py-8">
                      <Calendar className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500">No scheduled sessions for this date</p>
                      <p className="text-sm text-gray-400">Confirmed bookings will appear here</p>
                    </div>
                  ) : (
                    confirmedBookings.map((booking) => (
                      <div key={booking.id} className="p-4 bg-[#F5EFE7] border border-[#D4C4B0] rounded-lg">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start space-x-4">
                            {/* Customer Profile Picture */}
                            <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0">
                              {booking.customer_profile_picture ? (
                                <img
                                  src={booking.customer_profile_picture}
                                  alt={booking.client}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full bg-gradient-to-br from-[#9CAF88] to-[#7A8471] flex items-center justify-center text-white font-bold text-lg">
                                  {booking.client.split(' ').map(n => n[0]).join('')}
                                </div>
                              )}
                            </div>

                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-1">
                                <h4 className="font-semibold text-gray-900">{booking.client}</h4>
                                <span className="px-2 py-1 bg-[#9CAF88] text-[#FEFCF8] text-xs rounded-full font-medium">
                                  {booking.status}
                                </span>
                              </div>
                              <p className="text-sm text-gray-600 mb-3">{booking.service}</p>

                              {/* Contact Information */}
                              <div className="space-y-2 mb-3">
                                {/* Phone Number */}
                                {booking.customer_phone && (
                                  <div className="flex items-center text-sm text-gray-600">
                                    <svg className="h-4 w-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                    </svg>
                                    <span className="font-medium">{booking.customer_phone}</span>
                                  </div>
                                )}

                                {/* Location */}
                                {booking.customer_address && (
                                  <div className="flex items-start text-sm text-gray-600">
                                    <svg className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                    <div>
                                      <span className="font-medium text-gray-700">Location:</span>
                                      <span className="ml-1">{booking.customer_address}</span>
                                    </div>
                                  </div>
                                )}
                              </div>

                              {/* Customer Notes */}
                              {booking.notes && (
                                <div className="bg-white rounded-lg p-3 border border-gray-200">
                                  <div className="flex items-start">
                                    <svg className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012 2v8a2 2 0 01-2 2h-1l-4 4z" />
                                    </svg>
                                    <div>
                                      <span className="font-medium text-gray-700 text-sm">Session Notes:</span>
                                      <p className="text-sm text-gray-600 mt-1 italic">"{booking.notes}"</p>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="flex items-center space-x-4">
                            <span className="text-sm font-medium text-blue-600">{booking.time}</span>

                            {/* Add Messaging Icon */}
                            <MessagingIconSimple
                              targetUser={{
                                id: booking.customer_id,  // Use the actual customer ID
                                username: booking.client.replace(/\s+/g, '').toLowerCase(),
                                first_name: booking.client.split(' ')[0] || '',
                                last_name: booking.client.split(' ').slice(1).join(' ') || '',
                                profile_picture: booking.customer_profile_picture
                              }}
                              className="bg-white/20 backdrop-blur-sm border border-gray-200 hover:bg-white/30 transition-colors"
                            />

                            <button className="text-blue-500 hover:text-blue-700">
                              <Eye className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* Availability Tab */}
            {activeTab === 'availability' && (
              <TrainerAvailabilityManager />
            )}

            {/* Clients Tab */}
            {activeTab === 'clients' && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-6">Client Management</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {recentClients.map((client, index) => (
                    <div key={index} className="p-4 bg-[#F8F4EE] border border-[#B8C5A6] rounded-lg">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center space-x-3">
                          {/* Customer Profile Picture */}
                          <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0">
                            {client.profile_picture ? (
                              <img
                                src={client.profile_picture}
                                alt={client.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full bg-gradient-to-br from-[#E6A756] to-[#D2824C] flex items-center justify-center text-white font-bold text-lg">
                                {client.name.split(' ').map(n => n[0]).join('')}
                              </div>
                            )}
                          </div>
                          <div className="flex-1">
                            <h4 className="font-semibold text-gray-900">{client.name}</h4>
                            <p className="text-sm text-gray-600">{client.sessions} sessions completed</p>
                            <p className="text-xs text-gray-500">Last seen: {client.lastSeen}</p>
                          </div>
                        </div>

                        <div className="flex items-center space-x-3">
                          <div className="flex items-center space-x-1">
                            {[...Array(client.rating)].map((_, i) => (
                              <Star key={i} className="h-4 w-4 text-yellow-400 fill-current" />
                            ))}
                          </div>

                          {/* Add Messaging Icon - using client not booking */}
                          <MessagingIconSimple
                            targetUser={{
                              id: client.id || `client-${index}`, // You'll need to add client.id from backend
                              username: client.username || client.name.replace(/\s+/g, '').toLowerCase(),
                              first_name: client.name.split(' ')[0],
                              last_name: client.name.split(' ').slice(1).join(' ') || '',
                              profile_picture: client.profile_picture
                            }}
                            className="bg-white/20 backdrop-blur-sm border border-gray-200 hover:bg-white/30 transition-colors"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Earnings Tab */}
            {activeTab === 'earnings' && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-6">Earnings Overview</h3>
                {stripeConnected && (
                  <button
                    onClick={handleManagePayments}
                    className="flex items-center px-4 py-2 bg-[#8B5A3C] text-[#FEFCF8] rounded-lg hover:bg-[#5D4037] transition-colors text-sm font-medium"
                  >

                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    Manage Payment Settings
                  </button>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="p-4 bg-[#F8F4EE] border border-[#A0673D] rounded-lg">
                      <h4 className="font-semibold text-gray-900 mb-2">This Month</h4>
                      <p className="text-3xl font-bold text-[#A0673D]">${earningsData.month}</p>
                      <p className="text-sm text-gray-600">Keep up the great work!</p>
                    </div>
                    <div className="p-4 bg-[#F5EFE7] border border-[#E6A756] rounded-lg">
                      <h4 className="font-semibold text-gray-900 mb-2">Pending Payments</h4>
                      <p className="text-2xl font-bold text-[#E6A756]">${earningsData.pending}</p>
                      <p className="text-sm text-gray-600">Payments processing</p>
                    </div>
                  </div>
                  <div className="p-4 bg-gray-50 border rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-4">Performance Summary</h4>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm">Weekly Earnings</span>
                        <span className="font-semibold text-green-600">${earningsData.week}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm">Daily Average</span>
                        <span className="font-semibold text-blue-600">${Math.round(earningsData.week / 7)}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm">Active Clients</span>
                        <span className="font-semibold text-purple-600">{recentClients.length}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'pricing' && <TrainerPricingManager />}


            {activeTab === 'bookings' && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-6">My Bookings</h3>
                <BookingDashboard />
              </div>
            )}

            {/* Resources Tab */}
            {activeTab === 'resources' && <TrainerResourcesManager />}



          </div>
        </div>
      </div>
    </div >
  );
};

export default TrainerDashboard;